import { useTranslation } from 'react-i18next';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { useModal } from '@/components/providers/ModalProvider/ModalProvider';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import type { Party } from '@/features/party/types';
import useBroker from '@/hooks/useBroker';
import removeHtml from '@/lib/html/removeHtml';
import hasItems from '@/lib/utils/hasItems';

import ExportEmailModal from '../components/ExportEmailModal/ExportEmailModal';
import ExportPartyModal from '../components/ExportPartyModal/ExportPartyModal';
import PickCRMModal from '../components/PickCRMModal/PickCRMModal';
import type { CRM } from '../types';
import isPartyInCrm from '../utils/isPartyInCrm';

const CRMS_SUPPORT_EXPORTING = ['anva', 'assu', 'dias', 'brio'];

export default function useExportEmail() {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const { crms } = useBroker();

  const selectedParty = useSelectedParty();
  const {
    analysis: { analysis },
  } = useEmail();

  const exportingCrms =
    crms?.filter((crm) => CRMS_SUPPORT_EXPORTING.includes(crm.key)) ?? [];

  const showExportToCrmModal = (crm: CRM, party: Party) => {
    showModal(
      ExportEmailModal,
      {
        crm,
        defaultValues: {
          title: analysis?.subject,
          description: removeHtml(analysis?.summary_bullets?.[0]),
        },
        party,
      },
      {
        size: 'full',
        title: t('EXPORT_EMAIL_MODAL_TITLE', {
          crm: t(`CRM_NAME_${crm.toUpperCase()}`),
        }),
      },
    );
  };

  const pickCrmToExportTo = (party: Party) =>
    showModal(PickCRMModal, {
      onSubmit: (crm: CRM) => showExportToCrmModal(crm, party),
      description: t('PICK_CRM_EXPORT_EMAIL_DESCRIPTION'),
      availableCrms: exportingCrms.filter((crm) =>
        isPartyInCrm(party, crm.key),
      ),
    });

  const exportEmail = () => {
    if (!selectedParty) return;

    const isPartyInExportingCrm = exportingCrms.some((crm) =>
      isPartyInCrm(selectedParty, crm.key),
    );

    if (isPartyInExportingCrm) {
      pickCrmToExportTo(selectedParty);

      return;
    }

    showModal(ExportPartyModal, {
      partyId: selectedParty.id,
      crms: exportingCrms,
      titleKey: 'EXPORT_PARTY_NOT_FOUND_TITLE',
      onExported: pickCrmToExportTo,
    });
  };

  return { canExportEmail: hasItems(exportingCrms), exportEmail };
}
